import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import { AuthenticationToken } from '../entities/authentication-token.entity';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class WebApiService {
  private readonly KEY_URI_REFRESH = '/api/v1.1/authentication/token';

  constructor(private http: HttpClient, private tokenService: TokenService) {}

  /**
   * Gets url from defined uri
   * @param uri
   * @returns request url
   */
  public getUrl(uri: string = ''): string {
    //si l'url api n'existe pas dans config
    //remplacer le sous-domaine 'dashboard' par 'api' pour créer l'url de l'api
    return !environment.config.gatewayUrl
      ? window.location.href.replace(
          new RegExp('(https?://)dashboard.([^/]+)/.*$'),
          '$1api.$2'
        ) + uri
      : environment.config.gatewayUrl + uri;
  }

  /**
   * Sends a GET request to the specified URI.
   * If the user is logged in, automatically includes the authentication token.
   * @param uri
   * @param requireAuthentication
   * @param options
   * @returns request result
   */
  public get(
    uri: string,
    requireAuthentication = true,
    options: any = {}
  ): Observable<any> {
    let httpOptions = Object.assign(this.getOptions(), options);

    return this.refreshAccessToken(requireAuthentication).pipe(
      switchMap(() =>
        this.http
          .get(this.getUrl(uri), httpOptions)
          .pipe(
            catchError((res: any) =>
              throwError(() => new Error(this.getErrorType(res)))
            )
          )
      )
    );
  }

  /**
   * Sends a DELETE request to the specified URI.
   * If the user is logged in, automatically includes the authentication token.
   * @param uri
   * @param requireAuthentication
   * @returns request result
   */
  public delete(uri: string, requireAuthentication = true): Observable<any> {
    return this.refreshAccessToken(requireAuthentication).pipe(
      switchMap(() =>
        this.http
          .delete(this.getUrl(uri), this.getOptions())
          .pipe(
            catchError((res: any) =>
              throwError(() => new Error(this.getErrorType(res)))
            )
          )
      )
    );
  }

  /**
   * Sends a POST request to the specified URI.
   * If the user is logged in, automatically includes the authentication token.
   * @param uri
   * @param body
   * @param requireAuthentication
   * @returns request result
   */
  public post(
    uri: string,
    body: any,
    requireAuthentication = true
  ): Observable<any> {
    return this.refreshAccessToken(requireAuthentication).pipe(
      switchMap(() =>
        this.http
          .post(this.getUrl(uri), body, this.getOptions())
          .pipe(
            catchError((res: any) =>
              throwError(() => new Error(this.getErrorType(res)))
            )
          )
      )
    );
  }

  /**
   * Sends a PUT request to the specified URI.
   * If the user is logged in, automatically includes the authentication token.
   * @param uri
   * @param body
   * @param requireAuthentication
   * @returns request result
   */
  public put(
    uri: string,
    body: any = {},
    requireAuthentication = true
  ): Observable<any> {
    return this.refreshAccessToken(requireAuthentication).pipe(
      switchMap(() =>
        this.http
          .put(this.getUrl(uri), body, this.getOptions())
          .pipe(
            catchError((res: any) =>
              throwError(() => new Error(this.getErrorType(res)))
            )
          )
      )
    );
  }

  /**
   * Check if the error has a known status code and deserialize the error
   * into the proper type if required.
   * @param error
   * @returns error type
   */
  private getErrorType(error: any): any {
    let errorType = error;
    switch (error.status) {
      case 400:
        errorType = 'badrequest';
        break;
      case 401:
        errorType = 'unauthorized';
        break;
      case 403:
        errorType = 'forbidden';
        break;
      case 404:
        errorType = 'notfound';
        break;
      case 409:
        errorType = 'conflict';
        break;
      default:
        errorType = 'default';
        break;
    }
    return errorType;
  }

  /**
   * Generates the common request headers.
   * Specifies that all requests are serialized as JSON.
   * Includes the authorization token if the user is logged in.
   * @returns http options
   */
  private getOptions(): any {
    if (!this.tokenService.hasToken()) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json; chartset=utf-8',
      });
      const options = {
        headers: headers,
        responseType: 'json',
      };
      return options;
    } else {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json; chartset=utf-8',
        Authorization: 'Bearer ' + this.tokenService.getBearerToken(),
      });
      const options = {
        headers: headers,
        responseType: 'json',
      };
      return options;
    }
  }

  /**
   * Retrieves a new acces token not expirated from refresh token.
   * @param requiredAuthentication
   * @returns request result of refresh token
   */
  private refreshAccessToken(requiredAuthentication: boolean): Observable<any> {
    if (
      !requiredAuthentication ||
      !this.tokenService.hasRefreshToken() ||
      !this.tokenService.isAccessTokenExpired()
    ) {
      return of(true);
    }

    const body = {
      refreshToken: this.tokenService.getRefreshToken(),
    };
    const uri = this.KEY_URI_REFRESH;
    return this.http
      .post(this.getUrl(uri), JSON.stringify(body), this.getOptions())
      .pipe(catchError((res: any) => throwError(() => new Error(res))))
      .pipe(
        map((response: any) => {
          const token = new AuthenticationToken().deserialize(response.data);
          this.tokenService.updateAccessToken(token);
        })
      );
  }
}
